import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomDialog from "../../modals/CustomDialog/CustomDialog";
import { Button } from "../../forms/Buttons/Button";
import { DifficultyBadge, DIFFICULTY_LEVELS } from "../../Difficulty/Difficulty";
import { createFolderTask, getFolderAccess, getFolderMatrix, updateFolderTask } from "../../../services/questionService";
import { isCanceledError } from "../../../utils/examError";
import { focusRing } from "../matrix/MatrixParts";
import { compareRows, gradeLabel, gradeOf, levelsText, readMatrix, rowName, rowPlace, toNumber } from "../matrix/matrixModel";
import { byQuestions, MAX_TASK_COMMENT, MAX_TASK_COUNT, personName, taskErrorMessage, taskTopic, todayInput } from "./taskModel";
import TaskQuestionPicker from "./TaskQuestionPicker";

const fieldClass =
  "block w-full rounded-lg border-slate-300 text-sm shadow-sm transition duration-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15 disabled:bg-slate-50 disabled:text-slate-500";
const labelClass = "mb-1.5 block text-sm font-medium text-slate-800";
// Who may take a task of a kind (the server's TASK_RIGHTS: create for write, review for review).
const TAKES = { write: ["author", "expert", "moderator"], review: ["expert", "moderator"] };
const WRITERS = ["author", "expert", "moderator"];
const ROLE_ORDER = ["author", "expert", "moderator"];

const REVIEW_BY = ["authors", "questions"];

const emptyPlanned = () => ({ A: 0, B: 0, C: 0 });
// A review task's chosen questions as the picker keeps them: uuid → {uuid, code}.
const chosenOf = (task) => new Map((task?.questions || []).map((q) => [q.uuid, { uuid: q.uuid, code: q.code }]));
const sameSet = (a, b) => a.length === b.length && [...a].sort().join() === [...b].sort().join();
// What a row still needs given: its plan less what tasks already give out (never below 0).
const remainingOf = (row) => Object.fromEntries(DIFFICULTY_LEVELS.map((level) => [level, Math.max(0, toNumber(row?.planned?.[level]) - toNumber(row?.assigned?.[level]))]));

/**
 * A subject's task, given or changed by its moderator or an admin. `task`: the one to change (its kind, assignee, row
 * and whether a review is by authors or by questions stay); else a new one of `kind` ("write": questions of a matrix
 * row, the `row` given or one chosen here; "review": the questions of chosen authors, or chosen questions of the
 * subject). The dialog reads the subject's people (and its matrix, for a row to choose). `onSaved(task)` gets the saved
 * task.
 */
export default function TaskDialog({ isOpen, folder, task = null, kind: kindProp = "write", row: rowProp = null, onClose, onSaved }) {
  const [t] = useTranslation();
  const editing = !!task;
  const [kind, setKind] = useState(kindProp);
  const [members, setMembers] = useState(null);
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [assignee, setAssignee] = useState("");
  const [rowUuid, setRowUuid] = useState("");
  const [planned, setPlanned] = useState(emptyPlanned);
  const [authors, setAuthors] = useState(() => new Set());
  const [reviewBy, setReviewBy] = useState("authors");
  const [chosen, setChosen] = useState(() => new Map());
  const [due, setDue] = useState("");
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !folder) return undefined;
    setKind(task?.kind || kindProp);
    setAssignee(task?.assignee?.uuid || "");
    setRowUuid(task ? task.row || "" : rowProp?.uuid || "");
    setPlanned(task?.planned ? { ...emptyPlanned(), ...task.planned } : rowProp ? remainingOf(rowProp) : emptyPlanned());
    setAuthors(new Set((task?.authors || []).map((a) => a.uuid)));
    setReviewBy(byQuestions(task) ? "questions" : "authors");
    setChosen(chosenOf(task));
    setDue(task?.due_date || "");
    setComment(task?.comment || "");
    setError("");
    setLoadError("");
    setMembers(null);
    setRows(rowProp ? [rowProp] : null);
    const controller = new AbortController();
    getFolderAccess(folder.uuid, { signal: controller.signal })
      .then((data) => setMembers(Array.isArray(data?.members) ? data.members : []))
      .catch((err) => {
        if (!isCanceledError(err)) {
          setMembers([]);
          setLoadError(t("folders.tasks.people_failed"));
        }
      });
    // A new write task without a row given chooses one of the matrix's (those with a topic).
    if (!task && !rowProp) {
      getFolderMatrix(folder.uuid, { signal: controller.signal })
        .then((data) => setRows((readMatrix(data)?.rows || []).filter((r) => r.topic && r.uuid).sort(compareRows)))
        .catch((err) => {
          if (!isCanceledError(err)) setRows([]);
        });
    }
    return () => controller.abort();
  }, [isOpen, folder, task, kindProp, rowProp, t]);

  const row = useMemo(() => (rows || []).find((r) => r.uuid === rowUuid) || null, [rows, rowUuid]);
  // People by role, those who may take this kind of task.
  const takers = useMemo(
    () => (members || []).filter((m) => TAKES[kind].includes(m.role)).sort((a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role) || personName(a.user).localeCompare(personName(b.user))),
    [members, kind]
  );
  const writers = useMemo(
    () => (members || []).filter((m) => WRITERS.includes(m.role) && m.user.uuid !== assignee).sort((a, b) => personName(a.user).localeCompare(personName(b.user))),
    [members, assignee]
  );
  const rowGroups = useMemo(() => {
    const groups = new Map();
    (rows || []).forEach((r) => {
      const grade = gradeOf(r);
      if (!groups.has(grade)) groups.set(grade, []);
      groups.get(grade).push(r);
    });
    return [...groups];
  }, [rows]);

  const write = kind === "write";
  const pickQuestions = !write && reviewBy === "questions";
  const plannedTotal = DIFFICULTY_LEVELS.reduce((sum, level) => sum + toNumber(planned[level]), 0);
  const ready =
    !saving &&
    (editing || !!assignee) &&
    (write ? (editing || !!rowUuid) && plannedTotal > 0 : pickQuestions ? chosen.size > 0 : authors.size > 0) &&
    comment.length <= MAX_TASK_COMMENT;

  const chooseRow = (uuid) => {
    setRowUuid(uuid);
    setError("");
    const next = (rows || []).find((r) => r.uuid === uuid);
    if (next) setPlanned(remainingOf(next));
  };
  const setCount = (level, value) => {
    const n = value === "" ? 0 : Math.max(0, Math.min(MAX_TASK_COUNT, Math.floor(Number(value) || 0)));
    setPlanned((p) => ({ ...p, [level]: n }));
  };
  const toggleAuthor = (uuid) =>
    setAuthors((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!ready) return;
    setSaving(true);
    setError("");
    const counts = Object.fromEntries(DIFFICULTY_LEVELS.map((level) => [level, toNumber(planned[level])]));
    try {
      let saved;
      if (editing) {
        const fields = {};
        if (write && JSON.stringify(counts) !== JSON.stringify({ ...emptyPlanned(), ...task.planned })) fields.planned = counts;
        if (pickQuestions) {
          if (!sameSet([...chosen.keys()], (task.questions || []).map((q) => q.uuid))) fields.questions = [...chosen.keys()];
        } else if (!write && !sameSet([...authors], (task.authors || []).map((a) => a.uuid))) fields.authors = [...authors];
        if ((due || null) !== (task.due_date || null)) fields.due_date = due || null;
        if (comment !== (task.comment || "")) fields.comment = comment;
        saved = Object.keys(fields).length ? await updateFolderTask(folder.uuid, task.uuid, fields) : task;
      } else {
        const body = write
          ? { kind, assignee, row: rowUuid, planned: counts }
          : pickQuestions
            ? { kind, assignee, questions: [...chosen.keys()] }
            : { kind, assignee, authors: [...authors] };
        if (due) body.due_date = due;
        if (comment.trim()) body.comment = comment.trim();
        saved = await createFolderTask(folder.uuid, body);
      }
      onSaved(saved, !editing);
    } catch (err) {
      // A refusal of some chosen questions names them by their codes.
      const refused = err?.response?.data?.questions;
      const codes = Array.isArray(refused) ? refused.map((uuid) => chosen.get(uuid)?.code).filter(Boolean) : [];
      const message = taskErrorMessage(t, err, editing ? "folders.tasks.save_failed" : "folders.tasks.create_failed");
      setError(codes.length ? `${message} (${codes.join(", ")})` : message);
    } finally {
      setSaving(false);
    }
  };

  const loading = members === null || (write && !editing && rows === null);

  return (
    <CustomDialog
      isOpen={isOpen}
      closeModal={() => !saving && onClose()}
      headerText={editing ? t("folders.tasks.dialog.edit_title") : t("folders.tasks.dialog.new_title")}
      maxWidth={pickQuestions ? "max-w-2xl" : "max-w-xl"}
    >
      <form onSubmit={handleSave} className="mt-1 space-y-5 text-left" noValidate>
        <p className="text-sm text-slate-500">{folder?.name}</p>

        {!editing && (
          <div role="radiogroup" aria-label={t("folders.tasks.dialog.kind")} className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1">
            {["write", "review"].map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={kind === value}
                onClick={() => {
                  setKind(value);
                  setError("");
                  if (!TAKES[value].includes((members || []).find((m) => m.user.uuid === assignee)?.role)) setAssignee("");
                }}
                className={`min-h-[2.5rem] rounded-md px-3 text-sm font-medium transition duration-200 max-sm:min-h-11 ${focusRing} ${
                  kind === value ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {t(`folders.tasks.kind.${value}`)}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <p role="status" className="py-6 text-center text-sm text-slate-500">
            {t("folders.tasks.dialog.loading")}
          </p>
        ) : (
          <>
            {loadError && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{loadError}</p>}

            <div>
              <label htmlFor="task-assignee" className={labelClass}>
                {t("folders.tasks.dialog.assignee")}
              </label>
              {editing ? (
                <p className="text-sm text-slate-900">{personName(task.assignee)}</p>
              ) : (
                <>
                  <select
                    id="task-assignee"
                    value={assignee}
                    onChange={(e) => {
                      const next = e.target.value;
                      setAssignee(next);
                      // Nobody reviews their own questions.
                      setAuthors((prev) => new Set([...prev].filter((uuid) => uuid !== next)));
                      setChosen((prev) => new Map([...prev].filter(([, q]) => !next || q.author !== next)));
                      setError("");
                    }}
                    className={`${fieldClass} h-10 max-sm:h-11`}
                  >
                    <option value="">{t("folders.tasks.dialog.assignee_placeholder")}</option>
                    {ROLE_ORDER.filter((role) => takers.some((m) => m.role === role)).map((role) => (
                      <optgroup key={role} label={t(`folders.roles.${role}`)}>
                        {takers
                          .filter((m) => m.role === role)
                          .map((m) => (
                            <option key={m.user.uuid} value={m.user.uuid}>
                              {personName(m.user)}
                            </option>
                          ))}
                      </optgroup>
                    ))}
                  </select>
                  {members && takers.length === 0 && <p className="mt-1.5 text-xs text-amber-800">{t(`folders.tasks.dialog.no_takers_${kind}`)}</p>}
                </>
              )}
            </div>

            {write ? (
              <>
                <div>
                  <label htmlFor="task-row" className={labelClass}>
                    {t("folders.tasks.dialog.row")}
                  </label>
                  {editing || rowProp ? (
                    <p className="text-sm text-slate-900">
                      {editing ? taskTopic(t, task) : rowName(t, rowProp)}
                      <span className="text-slate-500"> · {editing ? gradeLabel(t, task.topic?.grade) : `${gradeLabel(t, gradeOf(rowProp))}, ${rowPlace(t, rowProp)}`}</span>
                    </p>
                  ) : rows.length === 0 ? (
                    <p className="text-sm text-amber-800">{t("folders.tasks.dialog.no_rows")}</p>
                  ) : (
                    <select id="task-row" value={rowUuid} onChange={(e) => chooseRow(e.target.value)} className={`${fieldClass} h-10 max-sm:h-11`}>
                      <option value="">{t("folders.tasks.dialog.row_placeholder")}</option>
                      {rowGroups.map(([grade, list]) => (
                        <optgroup key={grade} label={gradeLabel(t, grade)}>
                          {list.map((r) => (
                            <option key={r.uuid} value={r.uuid}>
                              {rowPlace(t, r)} — {rowName(t, r)}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  )}
                  {row && !editing && (
                    <p className="mt-1.5 text-xs text-slate-500">
                      {t("folders.tasks.dialog.row_plan", { plan: levelsText(row.planned), given: levelsText(row.assigned) })}
                    </p>
                  )}
                </div>

                <fieldset>
                  <legend className={labelClass}>{t("folders.tasks.dialog.planned")}</legend>
                  <div className="grid grid-cols-3 gap-3">
                    {DIFFICULTY_LEVELS.map((level) => (
                      <label key={level} className="flex flex-col items-start gap-1.5">
                        <DifficultyBadge level={level} />
                        <input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={MAX_TASK_COUNT}
                          value={planned[level]}
                          onChange={(e) => setCount(level, e.target.value)}
                          aria-label={t("folders.tasks.dialog.count_of", { level })}
                          className={`${fieldClass} h-10 tabular-nums max-sm:h-11`}
                        />
                      </label>
                    ))}
                  </div>
                  {row && !editing && DIFFICULTY_LEVELS.some((level) => toNumber(planned[level]) > remainingOf(row)[level]) && (
                    <p className="mt-1.5 text-xs text-amber-800">{t("folders.tasks.dialog.over_plan")}</p>
                  )}
                  <p className="mt-1.5 text-xs text-slate-500">{t("folders.tasks.dialog.write_rule")}</p>
                </fieldset>
              </>
            ) : (
              <>
                <p className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-900 ring-1 ring-inset ring-blue-600/10">
                  {t("folders.tasks.dialog.review_rule")}
                </p>
                {!editing && (
                  <div>
                    <p id="task-review-by" className={labelClass}>
                      {t("folders.tasks.dialog.review_by")}
                    </p>
                    <div role="radiogroup" aria-labelledby="task-review-by" className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1">
                      {REVIEW_BY.map((value) => (
                        <button
                          key={value}
                          type="button"
                          role="radio"
                          aria-checked={reviewBy === value}
                          onClick={() => {
                            setReviewBy(value);
                            setError("");
                          }}
                          className={`min-h-[2.5rem] rounded-md px-3 text-sm font-medium transition duration-200 max-sm:min-h-11 ${focusRing} ${
                            reviewBy === value ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-600 hover:text-slate-900"
                          }`}
                        >
                          {t(`folders.tasks.dialog.by_${value}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {pickQuestions ? (
                  <TaskQuestionPicker
                    folder={folder}
                    assignee={editing ? task.assignee?.uuid : assignee}
                    authors={writers}
                    chosen={chosen}
                    onChange={(next) => {
                      setChosen(next);
                      setError("");
                    }}
                  />
                ) : (
                  <fieldset>
                    <legend className={labelClass}>{t("folders.tasks.dialog.authors")}</legend>
                    {writers.length === 0 ? (
                      <p className="text-sm text-amber-800">{t("folders.tasks.dialog.no_authors")}</p>
                    ) : (
                      <>
                        <div className="mb-2 flex items-center justify-between gap-3 text-xs text-slate-500">
                          <span>{t("folders.tasks.dialog.authors_hint")}</span>
                          <button
                            type="button"
                            onClick={() => setAuthors(authors.size === writers.length ? new Set() : new Set(writers.map((m) => m.user.uuid)))}
                            className={`shrink-0 rounded px-1.5 py-1 font-medium text-blue-700 hover:bg-blue-50 ${focusRing}`}
                          >
                            {authors.size === writers.length ? t("folders.tasks.dialog.authors_none") : t("folders.tasks.dialog.authors_all")}
                          </button>
                        </div>
                        <ul className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-1.5">
                          {writers.map((m) => (
                            <li key={m.user.uuid}>
                              <label className="flex min-h-[2.5rem] cursor-pointer items-center gap-3 rounded-md px-2 hover:bg-slate-50 max-sm:min-h-11">
                                <input
                                  type="checkbox"
                                  checked={authors.has(m.user.uuid)}
                                  onChange={() => toggleAuthor(m.user.uuid)}
                                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-600"
                                />
                                <span className="min-w-0 flex-1 truncate text-sm text-slate-900">{personName(m.user)}</span>
                                <span className="shrink-0 text-xs text-slate-500">{t(`folders.roles.${m.role}`)}</span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </fieldset>
                )}
              </>
            )}

            <div className="grid gap-4 sm:grid-cols-[12rem_minmax(0,1fr)]">
              <div>
                <label htmlFor="task-due" className={labelClass}>
                  {t("folders.tasks.dialog.due")} <span className="font-normal text-slate-500">{t("folders.tasks.dialog.optional")}</span>
                </label>
                <input id="task-due" type="date" min={todayInput()} value={due} onChange={(e) => setDue(e.target.value)} className={`${fieldClass} h-10 max-sm:h-11`} />
              </div>
            </div>

            <div>
              <label htmlFor="task-comment" className={labelClass}>
                {t("folders.tasks.dialog.comment")} <span className="font-normal text-slate-500">{t("folders.tasks.dialog.optional")}</span>
              </label>
              <textarea
                id="task-comment"
                rows={3}
                value={comment}
                maxLength={MAX_TASK_COMMENT}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t("folders.tasks.dialog.comment_placeholder")}
                className={fieldClass}
              />
            </div>
          </>
        )}

        {error && (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" color="secondary-outline" text={t("buttons.cancel")} onClick={onClose} disabled={saving} className="max-sm:h-11" />
          <Button
            type="submit"
            color="primary"
            text={editing ? t("buttons.save") : t("folders.tasks.dialog.give")}
            loading={saving}
            disabled={!ready || loading}
            className="max-sm:h-11"
          />
        </div>
      </form>
    </CustomDialog>
  );
}
